import * as React from 'react';

import { cn } from '@/lib/utils';
import { sanitizeRichHtml } from './rich-html-sanitizer';

// =============================================================================
// rich-html-view.tsx — Rendu LECTURE SEULE de HTML riche, sanitizé (0 dépendance tierce).
// =============================================================================
// PARTAGÉ (S34-RICHTEXT-CTA #14, DBU §3.4 l.2724 « texte riche ») : rend un descriptif enrichi
// (titres, listes, gras/italique, liens, tables simples) en LECTURE — pendant read-only de
// `rich-html-editor.tsx`, avec le MÊME sanitizer (`sanitizeRichHtml`) que l'éditeur et l'écriture.
//
// ── SÉCURITÉ ──────────────────────────────────────────────────────────────────────────
// Défense en profondeur : le serveur nettoie déjà au write (choke point shared-kernel), MAIS le rendu
// re-nettoie au read (allowlist stricte : aucun `on*`, aucun `javascript:`, href http(s) seul). AUCUN
// `dangerouslySetInnerHTML` : l'injection passe par `ref.innerHTML` sur une valeur DÉJÀ nettoyée
// (même patron que l'éditeur) — l'analyseur JSX ne voit jamais de HTML brut.

export interface RichHtmlViewProps {
  /** HTML riche à rendre (sera re-nettoyé au read). */
  readonly html: string;
  /** Classes additionnelles du conteneur. */
  readonly className?: string;
  /** Libellé accessible optionnel (le contenu est décoratif structurel sinon). */
  readonly ariaLabel?: string;
}

/** Styles de rendu du contenu riche (miroir de la zone d'édition — titres/listes/liens/tables). */
const RICH_CONTENT_CLASS = cn(
  'max-w-none text-sm text-muted-foreground',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-foreground',
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-foreground',
  '[&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-foreground',
  '[&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_a]:text-primary [&_a]:underline',
  '[&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
);

export function RichHtmlView({
  html,
  className,
  ariaLabel,
}: RichHtmlViewProps): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const clean = React.useMemo(() => sanitizeRichHtml(html), [html]);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = clean;
    }
  }, [clean]);

  return (
    <div
      ref={ref}
      className={cn(RICH_CONTENT_CLASS, className)}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
    />
  );
}
